import { FiDownload } from "react-icons/fi";

import type { KeyPersonnelTableItem } from "newui/features/Company/KeyPersonnel/components/KeyPersonnelTable";
import PillDisplay from "newui/components/PillDisplay";
import { PROFILE_TYPE_CONSULTANT } from "utils/constants";

interface MobilePersonnelCardProps {
	person: KeyPersonnelTableItem;
	profileTypeId?: number;
	onClick: () => void;
}

/**
 * Compact card view of a key personnel member for mobile screens.
 * Replaces the wide table row on small viewports.
 */
export const MobilePersonnelCard: React.FC<MobilePersonnelCardProps> = ({
	person,
	profileTypeId,
	onClick,
}) => {
	const isConsultant = profileTypeId === PROFILE_TYPE_CONSULTANT;
	const initials = person.full_name
		.split(" ")
		.map((p) => p.charAt(0))
		.join("")
		.toUpperCase()
		.slice(0, 2);

	return (
		<button
			type="button"
			onClick={onClick}
			className="w-full rounded-lg border border-gray-200 bg-white p-4 text-left transition-shadow hover:shadow-md"
			data-testid={`mobile-personnel-card-${person.id}`}
		>
			<div className="flex items-center gap-3">
				{/* Avatar */}
				<div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gray-100">
					{person.avatar_location ? (
						<img
							src={person.avatar_location}
							alt={person.full_name}
							className="h-full w-full object-cover"
						/>
					) : (
						<span className="text-sm font-semibold text-gray-400">
							{initials}
						</span>
					)}
				</div>

				{/* Name & Position */}
				<div className="min-w-0 flex-1">
					<h4 className="truncate font-roboto text-sm font-semibold text-gray-900">
						{person.full_name}
					</h4>
					<p className="truncate text-xs text-gray-500">{person.position}</p>
				</div>

				{/* Experience badge */}
				{person.experience > 0 &&
					<div className="text-xs">
						<h5 className="font-bold pb-1">Experience</h5>
						<span className="shrink-0 rounded bg-gray-100 px-2 py-0.5 font-medium text-gray-600">
							{person.experience} yrs
						</span>
					</div>
				}
			</div>

			{/* Consultant: show first expertise group with its disciplines */}
			{isConsultant ? (
				<>
					{person.disciplinesFormatted && person.disciplinesFormatted.length > 0 && (() => {
						const firstGroup = person.disciplinesFormatted[0];
						const groupDisciplines = person.disciplines?.filter((d) =>
							firstGroup.disciplines.includes(d.id),
						) ?? [];
						const hasMore = person.disciplinesFormatted.length > 1;

						return (
							<div className="pt-2">
								<h5 className="text-xs font-bold">Expertise: {firstGroup.expertise}</h5>
								{groupDisciplines.length > 0 && (
									<div className="mt-1 flex flex-wrap gap-1">
										{groupDisciplines.map((d) => (
											<PillDisplay name={d.name} key={d.id} type="discipline" />
										))}
									</div>
								)}
								{hasMore && (
									<span className="mt-1.5 inline-block text-xs font-medium text-blue-600">
										Click for more detail
									</span>
								)}
							</div>
						);
					})()}
				</>
			) : (
				person.sectors.length > 0 && (
					<div>
						<h5 className="text-xs font-bold pt-2">Top Sectors</h5>
						<div className="mt-1 flex flex-wrap gap-1">
							{person.sectors.slice(0, 3).map((sector) => (
								<PillDisplay name={sector.name} key={sector.id} type="sector" />
							))}
						</div>
					</div>
				)
			)}

			{/* Resume link */}
			{person.resume.length > 0 && (
				<div className="mt-2">
					<a
						href={person.resume[0].file_path}
						target="_blank"
						rel="noopener noreferrer"
						className="inline-flex items-center gap-1 text-xs text-blue-600 underline decoration-blue-300 underline-offset-2 hover:text-blue-800"
						onClick={(e) => e.stopPropagation()}
					>
						<FiDownload size={12} />
						Resume
					</a>
				</div>
			)}
		</button>
	);
};
